import Loader from '@/components/Loader'
import { isEmbedded } from '@/config'
import { useDemoPostMessage } from '@/hooks/useDemoPostMessage'
import useKeyPress from '@/hooks/useKeyPress'
import React, { useEffect } from 'react'
import { useLocationsStore } from './stores/useLocationsStore'
import { useWidgetStore } from './stores/useWidgetStore'
import { logger } from './utils'
import { isDemoShowPopupEnabled } from '@/components/widget-overlay/PopupModal/PopupModal'
import DemoSiteBackdrop from '@/components/DemoSiteBackdrop'
import WidgetOverlay from '@/components/WidgetOverlay'

const FullScreenChat = React.lazy(() => import('@/components/WidgetUI'))
const PopupModal = React.lazy(() => import('@/components/widget-overlay/PopupModal/PopupModal'))
const ComponentPreview = React.lazy(() => import('@/components/ComponentPreview'))

export default function App() {
  const {
    initialize,
    initialized,
    isFloatingWidgetMode,
    floatingWidgetOpen,
    isClosing,
    closeFloatingWidget,
    urlAllowed,
    demo,
    demoAction,
    previewOptions,
  } = useWidgetStore()

  const widgetEnabled = useLocationsStore((state) => state.data?.enabled)

  // biome-ignore lint/correctness/useExhaustiveDependencies: only need to run once
  useEffect(() => {
    initialize()
  }, [])

  useDemoPostMessage()

  useKeyPress('Escape', () => closeFloatingWidget(), isFloatingWidgetMode && floatingWidgetOpen, {
    mustNotBeFocused: true,
    triggerOnce: true,
  })

  if (isFloatingWidgetMode && !initialized) {
    logger.log('Returning null because floating widget not enabled or initialized')
    return null
  }

  if (!initialized) {
    logger.log('Rendering Loader (not initialized)')
    return <Loader fullPage />
  }

  if (isEmbedded() && !demo && widgetEnabled !== true) {
    return null
  }

  if (demo && previewOptions.component) {
    logger.log('Rendering component preview', previewOptions)
    return (
      <React.Suspense fallback={<Loader fullPage />}>
        <ComponentPreview previewOptions={previewOptions} />
      </React.Suspense>
    )
  }

  const isDemoShowPopup = isDemoShowPopupEnabled(demo, demoAction)

  if (!isFloatingWidgetMode && !isDemoShowPopup) {
    return (
      <>
        <DemoSiteBackdrop />
        <React.Suspense fallback={<Loader fullPage />}>
          <FullScreenChat />
        </React.Suspense>
      </>
    )
  }

  const showOverlay = urlAllowed || floatingWidgetOpen || isClosing || demo

  return (
    <>
      <DemoSiteBackdrop />
      {showOverlay && (
        <>
          {!isDemoShowPopup && <WidgetOverlay />}
          <React.Suspense fallback={null}>
            <PopupModal />
          </React.Suspense>
        </>
      )}
      {(floatingWidgetOpen || isClosing) && (
        <React.Suspense fallback={null}>
          <FullScreenChat />
        </React.Suspense>
      )}
    </>
  )
}
